import type { Product } from '@/domain/Product.ts';
import * as ProductService from '@/widgets/services/ProductService.ts';
import * as ContentService from '@/widgets/services/ContentService.ts';
import * as MarketService from '@/widgets/services/MarketService.ts';
import * as CategoryService from '@/widgets/services/CategoryService.ts';

import {
  resolveWidgetConfig,
  type WidgetConfig
} from '@/domain/WidgetConfig.ts';
import type { PLPRoute } from '@/domain/Route.ts';
import type { Market } from '@/domain/Market.ts';
import isPreview from '@/widgets/utils/isPreview.ts';
import type { Categories } from '@/domain/Category';
import { type ProductTranslations } from '@/domain/config/index.ts';
import type { CustomerProductRules } from '@/domain/assemblers/ProductAssembler';

export async function getRemoteData(route: PLPRoute): Promise<{
  marketConfig: Market;
  products: Product[];
  categories: Categories;
  config: WidgetConfig;
  productTranslations?: ProductTranslations;
  customerProductRules: CustomerProductRules;
}> {
  const { market, channel, productType, locale, technology } = route;
  const withCacheBusting = isPreview();

  const marketConfig = await MarketService.getMarketUsingNAPI();
  const customerProductRules = await ProductService.getCustomerProductRules(
    market,
    channel,
    locale,
    withCacheBusting
  );
  const [products, config, categories, enrichments] = await Promise.all([
    ProductService.getProducts(
      market,
      channel,
      productType,
      locale,
      technology,
      customerProductRules,
      withCacheBusting
    ),
    ContentService.getProductListConfig(
      market,
      channel,
      locale,
      withCacheBusting
    ),
    CategoryService.getCategories(market, channel, locale, withCacheBusting),
    ContentService.getProductEnrichmentsDefinition(
      market,
      channel,
      locale,
      withCacheBusting
    )
  ]);

  return {
    marketConfig,
    products,
    categories,
    config: resolveWidgetConfig(config, technology, productType),
    productTranslations: enrichments.translations,
    customerProductRules
  };
}
